import React, { useEffect, useState } from 'react';
import { soundtrack } from '../../Audio/Soundtrack';

/** Whether the browser lets sound play yet (after the first click or key), and whether it is muted. */
export function useSound() {
    const read = () => ({ running: soundtrack.running, muted: soundtrack.muted });
    const [state, setState] = useState(read);
    useEffect(() => soundtrack.onChange(() => setState(read())), []);
    return state;
}

// The sound switch under the name, on the loading screen (ink on paper) and in the room (light on the 3D).
// Until the browser allows sound it is only a quiet label, SOUND OFF with a flat meter; once sound plays it becomes
// an outlined button that mutes and unmutes. It acts on mousedown (and Enter / Space) and carries
// id="prevent-click" so the camera ignores the press.
const SoundSwitch: React.FC<{ tone: 'ink' | 'light' }> = ({ tone }) => {
    const { running, muted } = useSound();
    const off = !running || muted;
    const toggle = () => { if (running) soundtrack.setMuted(!muted); };

    return (
        <button
            type="button"
            id="prevent-click"
            className={`sound-toggle is-${tone}` + (running ? '' : ' is-locked') + (off ? ' is-muted' : '')}
            aria-pressed={running ? !muted : undefined}
            aria-label={!running ? 'Sound starts after the first click or key' : muted ? 'Turn sound on' : 'Turn sound off'}
            tabIndex={running ? 0 : -1}
            onMouseDown={(event) => { event.preventDefault(); toggle(); }}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); }
            }}
        >
            <span className="sound-meter" aria-hidden="true">
                <i /><i /><i /><i />
            </span>
            {/* both labels share one cell, so the button is always as wide as the longer one */}
            <span className="sound-label">
                <span className={off ? '' : 'is-shown'}>SOUND ON</span>
                <span className={off ? 'is-shown' : ''}>SOUND OFF</span>
            </span>
        </button>
    );
};

export default SoundSwitch;
